/**
 * SocialAuthButtons — "Continue with Google / Apple" (2026-08-11).
 *
 * Renders NOTHING until /auth/oauth/configured reports a provider is live
 * (env-gated server-side), so preview/unconfigured environments show no dead
 * buttons. Google uses Google Identity Services (renders its own button);
 * Apple uses the appleid.auth JS popup flow (needs APPLE_SERVICES_ID and the
 * page origin registered as a Return URL in the Apple Developer portal).
 *
 * The backend (routes/identity.py) verifies the provider's `id_token`; when
 * the email already belongs to a password account it returns
 * {link_required, confirmation_token} — we surface that so the user knows to
 * sign in with their password to connect the provider.
 */
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../lib/api';
import { Button } from './ui/button';

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const APPLE_SRC = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded) return resolve();
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
      return undefined;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => { s.dataset.loaded = '1'; resolve(); };
    s.onerror = reject;
    document.head.appendChild(s);
    return undefined;
  });
}

export const SocialAuthButtons = ({ onError, onMfaRequired }) => {
  const { oauthLogin, oauthLinkVerified } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  // 2026-09-17 — pending password-less link offer {token, provider}.
  const [linkOffer, setLinkOffer] = useState(null);
  const googleDivRef = useRef(null);
  const gisInitedRef = useRef(false);
  const appleInFlightRef = useRef(false);  // 2026-08-13 audit — reentrancy guard

  useEffect(() => {
    let cancelled = false;
    authAPI.oauthConfigured()
      .then((res) => { if (!cancelled) setCfg(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleResult = (data, provider) => {
    if (data?.mfa_required) {
      // 2026-08-13 audit SEC-001 — route to the shared MFA-code step.
      onMfaRequired?.(data);
      return;
    }
    if (data?.link_required) {
      // 2026-09-17 — provider-hosted mailbox (gmail/icloud): offer to link
      // WITHOUT the password. Otherwise keep the password-first guidance.
      if (data.verified_link_available && data.confirmation_token) {
        setLinkOffer({ token: data.confirmation_token, provider });
        return;
      }
      onError?.(
        `An account with this email already exists. Sign in with your password, `
        + `then connect ${provider} from Settings.`,
      );
    }
  };

  const surface = (err, provider) => {
    const detail = err?.response?.data?.detail;
    onError?.(
      (typeof detail === 'string' ? detail : detail?.message)
      || `${provider} sign-in failed. Please try again.`,
    );
  };

  const acceptLinkOffer = async () => {
    if (!linkOffer) return;
    setBusy(true);
    onError?.('');
    try {
      const data = await oauthLinkVerified(linkOffer.token, linkOffer.provider.toLowerCase());
      setLinkOffer(null);
      if (data?.mfa_required) onMfaRequired?.(data);
    } catch (err) {
      surface(err, linkOffer.provider);
    } finally {
      setBusy(false);
    }
  };

  // Google Identity Services button (init exactly once per mount; re-init
  // after the link offer card hands the row back).
  useEffect(() => {
    if (linkOffer) return undefined;
    if (!cfg?.google || !cfg.google_client_id || !googleDivRef.current) return undefined;
    if (gisInitedRef.current) return undefined;
    let cancelled = false;
    loadScript(GIS_SRC).then(() => {
      if (cancelled || gisInitedRef.current || !window.google?.accounts?.id
          || !googleDivRef.current) return;
      gisInitedRef.current = true;
      window.google.accounts.id.initialize({
        client_id: cfg.google_client_id,
        callback: async (response) => {
          try {
            setBusy(true);
            onError?.('');
            const data = await oauthLogin('google', response.credential);
            handleResult(data, 'Google');
          } catch (err) {
            surface(err, 'Google');
          } finally {
            setBusy(false);
          }
        },
      });
      window.google.accounts.id.renderButton(googleDivRef.current, {
        theme: 'outline', size: 'large', width: 320, text: 'continue_with',
      });
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg, linkOffer]);

  const handleApple = async () => {
    // 2026-08-13 security audit (P2) — synchronous re-entrancy guard. React
    // `busy` state updates async, so a fast double-tap could open two Apple
    // popups (one may hang / overwrite the other's error). A ref flips
    // instantly so the second tap is a no-op.
    if (appleInFlightRef.current) return;
    appleInFlightRef.current = true;
    onError?.('');
    try {
      setBusy(true);
      await loadScript(APPLE_SRC);
      window.AppleID.auth.init({
        clientId: cfg.apple_services_id,
        scope: 'name email',
        redirectURI: `${window.location.origin}/`,
        usePopup: true,
      });
      const res = await window.AppleID.auth.signIn();
      const data = await oauthLogin('apple', res.authorization.id_token);
      handleResult(data, 'Apple');
    } catch (err) {
      // Popup dismissed by the user is not an error worth surfacing.
      if (err?.error && err.error !== 'popup_closed_by_user') surface(err, 'Apple');
      else if (!err?.error) surface(err, 'Apple');
    } finally {
      appleInFlightRef.current = false;
      setBusy(false);
    }
  };

  if (!cfg || (!cfg.google && !cfg.apple_web)) return null;

  if (linkOffer) {
    return (
      <div
        className="mb-3 rounded-2xl border border-[hsl(var(--accent)/0.35)] bg-[hsl(var(--accent)/0.08)] p-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-300"
        data-testid="social-link-offer"
      >
        <p className="text-sm font-semibold leading-tight">This email already has an account</p>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Continue with {linkOffer.provider} to sign in — your old password will be turned off
          (set a new one anytime via &ldquo;Forgot your password?&rdquo;). Remember it? Sign in with your
          password instead and connect {linkOffer.provider} from your profile.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            className="flex-1"
            onClick={acceptLinkOffer}
            disabled={busy}
            data-testid="social-link-offer-accept"
          >
            {busy ? 'Connecting…' : `Continue with ${linkOffer.provider} instead`}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => { gisInitedRef.current = false; setLinkOffer(null); }}
            disabled={busy}
            data-testid="social-link-offer-use-password"
          >
            Use my password
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 mb-3" data-testid="social-auth-buttons">
      {cfg.google && cfg.google_client_id && (
        <div ref={googleDivRef} className="flex justify-center" data-testid="social-google-button" />
      )}
      {cfg.apple_web && cfg.apple_services_id && (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={handleApple}
          disabled={busy}
          data-testid="social-apple-button"
        >
          <svg viewBox="0 0 384 512" className="h-4 w-4 mr-2 fill-current" aria-hidden="true">
            <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
          </svg>
          Continue with Apple
        </Button>
      )}
    </div>
  );
};

export default SocialAuthButtons;
